/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import React, { useCallback, useEffect, useRef } from 'react';
import { Platform, Pressable, StyleSheet } from 'react-native';

export const EMAIL_DOMAIN_FIELD_TEST_ID = 'email-domain-field';

export type EmailDomainDismissOverlayViewProps = {
  visible: boolean;
  onPress: () => void;
  testID?: string;
};

const isEventInsideEmailDomainField = (target: EventTarget | null) => {
  if (!(target instanceof Element)) {
    return false;
  }

  return Boolean(
    target.closest(
      `[data-testid="${EMAIL_DOMAIN_FIELD_TEST_ID}"], [testID="${EMAIL_DOMAIN_FIELD_TEST_ID}"]`,
    ),
  );
};

/** Full-screen tap target that closes the email domain dropdown. */
export function EmailDomainDismissOverlayView({
  visible,
  onPress,
  testID = 'email-domain-dismiss-overlay',
}: EmailDomainDismissOverlayViewProps) {
  const hasDismissedRef = useRef(false);

  const handleDismiss = useCallback(() => {
    if (hasDismissedRef.current) {
      return;
    }
    hasDismissedRef.current = true;
    onPress();
  }, [onPress]);

  useEffect(() => {
    if (!visible) {
      hasDismissedRef.current = false;
    }
  }, [visible]);

  useEffect(() => {
    if (!visible || Platform.OS !== 'web') {
      return;
    }

    const doc = globalThis.document;
    if (!doc?.addEventListener) {
      return;
    }

    const onPointerDown = (event: Event) => {
      if (isEventInsideEmailDomainField(event.target)) {
        return;
      }
      handleDismiss();
    };

    doc.addEventListener('pointerdown', onPointerDown, true);
    return () => doc.removeEventListener('pointerdown', onPointerDown, true);
  }, [visible, handleDismiss]);

  if (!visible) {
    return null;
  }

  return (
    <Pressable
      testID={testID}
      accessibilityRole="button"
      style={styles.overlay}
      onPress={handleDismiss}
    />
  );
}

const styles = StyleSheet.create({
  overlay: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 1,
  },
});
